/**
 * Public Raises API client (sub-project ③B). Talks to the ③A tiered read API:
 *  - `GET /api/raises`          → directory list (tier-scoped bodies + envelope,
 *                                  plus contextual facets with `includeFacets=true`)
 *  - `GET /api/raises/:slug`    → tier-scoped project detail + contact preview
 *  - `GET /api/companies/:slug` → company profile + teaser project rollup
 *
 * ANONYMOUS-FRIENDLY BY DESIGN. The endpoints are mounted behind `optionalAuth`
 * (not `checkAuth`), so they serve anonymous traffic and never 401. We reuse
 * `createAuthHeaders`, which attaches a Bearer token ONLY when a user is signed
 * in and is a no-op otherwise — so the same client works logged-out (anonymous
 * tier) and logged-in (free/entitled tier). The SERVER decides the tier from the
 * presence of a valid token; the client never computes its own tier and renders
 * only what the API returns.
 *
 * Mirrors `activeRaisesApi.ts`:
 *  - `{ success, data }` envelope unwrap,
 *  - readable error extraction on non-2xx,
 *  - array filters serialize to comma-separated query params; empty / undefined /
 *    null params are omitted (`buildRaisesQueryString`).
 *
 * The inner `data` shape differs per endpoint (the directory list returns the
 * `tier`/`items`/`connectAvailable` envelope; the detail returns the
 * `ProjectDetail` envelope), so each method types its own `data` projection —
 * there is no second generic unwrap, only the outer `{ success, data }` one.
 */

import {
  assertAuthIdentity,
  captureAuthIdentity,
  createAuthHeaders,
  fetchWithActivity,
  getApiBaseUrl,
} from '@/utils/apiUtils';
import { z } from 'zod';
import type { InvestorCapitalStackDto } from '@/components/raises/capitalStack/types';
import type { DataroomViewAs } from '@/types/ownerFiles';
import type {
  Company,
  CompanyDetail,
  DirectoryCapitalAggregate,
  DirectoryQuery,
  DirectoryResponse,
  RaiseDetail,
  RaiseViewAs,
} from '@/types/raises';

const directoryCapitalAggregateSchema: z.ZodType<DirectoryCapitalAggregate> = z
  .object({
    capitalSeekingBand: z
      .enum(['$100B+', '$10B+', '$1B+', '$100M+', '$10M+', '$1M+', 'Under $1M'])
      .nullable(),
  })
  .strict();

/** The outer transport envelope every ③A route returns on success. */
interface ApiEnvelope<T> {
  success: true;
  data: T;
}

/**
 * Serializes a {@link DirectoryQuery} into a `?key=value` query string.
 *
 * Mirrors the admin `buildQueryString` contract the directory router depends on:
 *  - array filters (`region`, `country`, `sector`, `stage`) → comma-separated values,
 *  - `undefined`, `null`, empty strings, and empty arrays are omitted entirely,
 *  - scalars (string / number / boolean) are stringified.
 *
 * Returns `''` when nothing is set (the default page). Iterating the value space
 * (rather than a hard-coded key list) keeps this in lock-step with
 * {@link DirectoryQuery} — a new optional filter serializes automatically.
 */
export function buildRaisesQueryString(query: DirectoryQuery): string {
  const search = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      search.set(key, value.join(','));
      return;
    }
    search.set(key, String(value));
  });

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
}

/** Best-effort human-readable message from an arbitrary error body. */
function errorText(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.message === 'string') return record.message;
    if (typeof record.error === 'string') return record.error;
    if (typeof record.error === 'object') return errorText(record.error);
    try {
      return JSON.stringify(record);
    } catch {
      return null;
    }
  }
  return String(value);
}

/** Reads a readable error message from a non-2xx response (JSON `{ error }` or text). */
async function readError(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const body = await response.json().catch(() => null);
    return (
      errorText((body as { error?: unknown } | null)?.error ?? body) ||
      `Raises request failed (${response.status})`
    );
  }

  const text = await response.text().catch(() => '');
  return text || `Raises request failed (${response.status})`;
}

/**
 * The "not found" sentinel the slug methods raise on a 404 so a caller (the
 * React Query hook) can distinguish a genuinely-missing slug (render a 404 page)
 * from a transient/transport error (render an error state + retry). Carries the
 * status code so `useQuery`'s `retry` predicate can skip retrying 404s.
 */
export class RaisesNotFoundError extends Error {
  readonly status = 404;
  constructor(message = 'Not found') {
    super(message);
    this.name = 'RaisesNotFoundError';
  }
}

/**
 * Issues a GET against a public ③A path and unwraps the outer `{ success, data }`
 * envelope to the inner `data` (the per-endpoint projection). Anonymous-friendly:
 * `createAuthHeaders` attaches a token only when one exists.
 *
 * A 404 becomes a {@link RaisesNotFoundError} (missing slug); any other non-2xx
 * becomes a generic `Error` with the server's message.
 */
async function getJson<T>(path: string): Promise<T> {
  // §4.3 identity safety (CX#7): capture ONE identity snapshot at entry and thread it through
  // header construction, delivery, and every post-body-read fence. These routes are anonymous-
  // friendly (optionalAuth), so the snapshot is often `null` — which the shared apiUtils logic
  // treats as "nothing to protect" and no-ops, so a genuine anonymous read is unaffected. When a
  // user IS signed in (free/entitled tier), the same threading prevents A's tier body from being
  // delivered into B's state after an account switch.
  const requestIdentity = captureAuthIdentity();
  const headers = await createAuthHeaders({}, 'GET', requestIdentity);
  const response = await fetchWithActivity(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    headers,
  }, requestIdentity);

  if (!response.ok) {
    const status = response.status;
    const message = await readError(response);
    assertAuthIdentity(requestIdentity);
    if (status === 404) {
      throw new RaisesNotFoundError(message);
    }
    throw new Error(message);
  }

  const json = (await response.json()) as ApiEnvelope<T>;
  assertAuthIdentity(requestIdentity);
  return json.data;
}

/** URL-safe slug for a path segment (slugs are already kebab-case, but be defensive). */
function encodeSlug(slug: string): string {
  return encodeURIComponent(slug);
}

/**
 * Appends an ADMIN-ONLY `?viewAs=` override to a path when one is supplied. The
 * server ignores it for a non-admin and 400s an unrecognized value; the client
 * only ever attaches it from the admin "view-as perspective" control. Omitted
 * entirely when `viewAs` is absent → the caller's real, un-overridden view.
 */
function withViewAs(path: string, viewAs?: string): string {
  if (!viewAs) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}viewAs=${encodeURIComponent(viewAs)}`;
}

export const raisesApi = {
  /** GET /api/raises/aggregate — one validated coarse band, never a precise total. */
  getDirectoryCapitalAggregate: async (): Promise<DirectoryCapitalAggregate> =>
    directoryCapitalAggregateSchema.parse(
      await getJson<unknown>('/api/raises/aggregate'),
    ),

  /**
   * GET /api/raises — the tier-aware directory list. Returns the inner envelope
   * (`tier`, `items`, `connectAvailable`, `total`, `limit`, `offset`, and
   * `facets` when `includeFacets` is set); the server decides tier and the items
   * are teaser- or full-bodies accordingly.
   */
  listProjects: (query: DirectoryQuery = {}): Promise<DirectoryResponse> =>
    getJson<DirectoryResponse>(`/api/raises${buildRaisesQueryString(query)}`),

  /**
   * GET /api/raises/:slug — the tier-scoped detail envelope. A discriminated union
   * ({@link RaiseDetail}): a standard project (`kind:'standard'`) OR a Teaser Raise
   * (`kind:'teaser_raise'`, ⑩). Throws {@link RaisesNotFoundError} when the slug is
   * missing / a duplicate.
   *
   * `viewAs` is the ADMIN-ONLY DTO-tier override (spec: admin "view-as
   * perspective"): the server recomputes the SAME envelope shape for that audience
   * tier (faithful redaction — `anonymous` is a genuine teaser). Ignored for a
   * non-admin; omit it for the caller's real view.
   */
  getProject: (slug: string, viewAs?: RaiseViewAs): Promise<RaiseDetail> =>
    getJson<RaiseDetail>(withViewAs(`/api/raises/${encodeSlug(slug)}`, viewAs)),

  /**
   * GET /api/companies/:slug — the company profile envelope (facts + teaser
   * project rollup). Unwraps the `{ tier, company, connectAvailable }` data and
   * returns just the {@link Company} body (the company surface is teaser-only at
   * every tier). Throws {@link RaisesNotFoundError} for a missing slug.
   */
  getCompany: async (slug: string): Promise<Company> => {
    const detail = await getJson<CompanyDetail>(`/api/companies/${encodeSlug(slug)}`);
    return detail.company;
  },

  /**
   * GET /api/raises/:slug/capital-stack — the LATEST OWNER-VISIBLE capital-stack run
   * for a raise, read-only + generation-free. This is the PUBLIC, tier-gated read the
   * entitled investor view (and the admin "view-as owner" preview) consume — NOT the
   * owner-gated `/api/owner/...` route (which 403s any non-owner).
   *
   * The server authorizes SERVER-SIDE: it serves the run ONLY to an entitled viewer,
   * the verified owner, or an admin, and 404s everyone else — deliberately NOT oracling
   * whether a run exists for a raise the caller may not see. Because "no visible run"
   * (`data:null`) and "not permitted" (404) both mean "show nothing" to a read-only
   * viewer, we map the 404 to `null` here rather than surfacing an error: the module
   * simply does not render. Any OTHER non-2xx propagates as a genuine error.
   *
   * `viewAs` is the ADMIN-ONLY canonical-admin PREVIEW override (round-9 #7). The admin
   * "view-as owner" preview reads this PUBLIC route (the owner-gated route would 403 an
   * admin who is not the verified owner), but R-E removed the `role==='admin' → entitled`
   * tier shortcut — so a canonical admin now resolves `free` here and would 404 with a
   * BARE URL. `?viewAs=owner` authorizes the SAME reduced read for a CANONICAL admin
   * (server re-verifies via the fail-closed Firestore-role seam; the token role is never
   * trusted). Omitted for a real entitled viewer — the server serves their true tier.
   */
  getPublicCapitalStack: async (
    slug: string,
    viewAs?: DataroomViewAs,
  ): Promise<InvestorCapitalStackDto | null> => {
    try {
      return await getJson<InvestorCapitalStackDto | null>(
        withViewAs(`/api/raises/${encodeSlug(slug)}/capital-stack`, viewAs),
      );
    } catch (error) {
      // A 404 (missing raise OR viewer not permitted) is indistinguishable from "no
      // published run" for a read-only viewer — render nothing, not an error.
      if (error instanceof RaisesNotFoundError) return null;
      throw error;
    }
  },
};

export default raisesApi;
